"use client";

import * as React from "react";
import {
	type Announcements,
	DndContext,
	DragOverlay,
	KeyboardSensor,
	PointerSensor,
	pointerWithin,
	rectIntersection,
	useSensor,
	useSensors,
	type CollisionDetection,
	type DraggableAttributes,
	type DraggableSyntheticListeners,
	type DragEndEvent,
	type DragStartEvent,
	type UniqueIdentifier,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
	flexRender,
	getCoreRowModel,
	useReactTable,
	type Row,
} from "@tanstack/react-table";
import { GripVerticalIcon } from "lucide-react";

import { cn } from "../../lib/utils";
import { type AnyColumnDef } from "./data-table-types";
import { AUTOSIZING_DEFAULT_COLUMN, getColumnWidth } from "./data-table-utils";
import { Skeleton } from "./skeleton";
import { Table } from "./table";

export type SectionedSortableSection<TRow> = {
	id: string;
	header: React.ReactNode;
	rows: TRow[];
	/** Freezes this section's own drag handle. Its rows stay draggable. */
	disabled?: boolean;
	/** Names the section to screen readers. Falls back to the id, which is rarely readable. */
	label?: string;
};

/**
 * A section and row ordering, held while a reorder is being persisted.
 *
 * It records ids only. Row and section content always comes from the `sections` prop, so an
 * edit made while an order is pending still renders.
 */
export type SectionedSortableOrder = {
	sectionIds: string[];
	rowIdsBySection: Record<string, string[]>;
};

export type SectionedSortableDrop<TRow> = {
	/** The sections as they look after the drop. */
	sections: SectionedSortableSection<TRow>[];
	/** Id of the section that moved, or of the section the row belongs to. */
	sectionId: string;
	/** 0-based index the moved section or row lands at. */
	toIndex: number;
} & ({ kind: "section"; rowId?: undefined } | { kind: "row"; rowId: string });

export type SectionedSortableTableProps<TRow> = {
	sections: SectionedSortableSection<TRow>[];
	columns: AnyColumnDef<TRow>[];
	/** Must be unique across ALL sections. */
	getRowId: (row: TRow) => string;
	/** Names a row to screen readers. Falls back to its id, which is rarely readable. */
	getRowLabel?: (row: TRow) => string;
	onReorderSection: (
		sectionId: string,
		toIndex: number,
		restore: () => void,
	) => void;
	onReorderRow: (
		rowId: string,
		sectionId: string,
		toIndex: number,
		restore: () => void,
	) => void;
	renderSectionActions?: (
		section: SectionedSortableSection<TRow>,
	) => React.ReactNode;
	renderRowActions?: (
		row: TRow,
		section: SectionedSortableSection<TRow>,
	) => React.ReactNode;
	/** Freezes both drag levels, e.g. while a reorder is in flight. */
	isDisabled?: boolean;
	/** Freezes row dragging only. Section headers stay draggable. */
	areRowsDisabled?: boolean;
	isLoading?: boolean;
	loadingRowCount?: number;
	emptyState?: React.ReactNode;
	/** Merged over the built-in drag announcements. */
	accessibility?: React.ComponentProps<typeof DndContext>["accessibility"];
	"data-testid"?: string;
};

// dnd ids are namespaced so a section and a row can never collide, and so a drop target can
// be classified without a lookup.
const SECTION_PREFIX = "section:";
const ROW_PREFIX = "row:";

const sectionDragId = (id: string) => `${SECTION_PREFIX}${id}`;
const rowDragId = (id: string) => `${ROW_PREFIX}${id}`;
const isSectionDragId = (id: UniqueIdentifier) =>
	String(id).startsWith(SECTION_PREFIX);
const stripSectionDragId = (id: UniqueIdentifier) =>
	String(id).slice(SECTION_PREFIX.length);
const stripRowDragId = (id: UniqueIdentifier) =>
	String(id).slice(ROW_PREFIX.length);

const orderOf = <TRow,>(
	sections: SectionedSortableSection<TRow>[],
	getRowId: (row: TRow) => string,
): SectionedSortableOrder => ({
	sectionIds: sections.map((section) => section.id),
	rowIdsBySection: Object.fromEntries(
		sections.map((section) => [section.id, section.rows.map(getRowId)]),
	),
});

const sameIds = (a: string[], b: string[]) =>
	a.length === b.length && a.every((id, index) => id === b[index]);

const sameOrder = (a: SectionedSortableOrder, b: SectionedSortableOrder) =>
	sameIds(a.sectionIds, b.sectionIds) &&
	a.sectionIds.every((sectionId) =>
		sameIds(
			a.rowIdsBySection[sectionId] ?? [],
			b.rowIdsBySection[sectionId] ?? [],
		),
	);

/** An optimistic arrangement, and the incoming arrangement it was applied on top of. */
type PendingOrder = {
	baseline: SectionedSortableOrder;
	override: SectionedSortableOrder;
};

/**
 * Lays a pending order over the sections a consumer passed in.
 *
 * Content is always read from `sections`, so an edit that lands while a reorder is still being
 * persisted still renders. An order whose ids no longer match the incoming ones is ignored, so
 * an add or a delete drops the override rather than pinning a stale arrangement.
 */
export function applySectionedSortableOrder<TRow>(
	sections: SectionedSortableSection<TRow>[],
	getRowId: (row: TRow) => string,
	order: SectionedSortableOrder | null,
): SectionedSortableSection<TRow>[] {
	if (!order) {
		return sections;
	}

	const sectionsById = new Map(
		sections.map((section) => [section.id, section]),
	);
	if (
		sectionsById.size !== order.sectionIds.length ||
		order.sectionIds.some((id) => !sectionsById.has(id))
	) {
		return sections;
	}

	return order.sectionIds.map((sectionId) => {
		const section = sectionsById.get(sectionId)!;
		const wantedRowIds = order.rowIdsBySection[sectionId] ?? [];
		const rowsById = new Map(section.rows.map((row) => [getRowId(row), row]));

		if (
			rowsById.size !== wantedRowIds.length ||
			wantedRowIds.some((rowId) => !rowsById.has(rowId))
		) {
			return section;
		}

		return {
			...section,
			rows: wantedRowIds.map((rowId) => rowsById.get(rowId)!),
		};
	});
}

/**
 * Works out what a drop means, or returns null when it means nothing.
 *
 * Rows reorder within their own section only: a row released over another section is refused
 * and snaps back. A row released over its own section header goes to the TOP of that section:
 * the header renders above its rows, so that gesture is "move to the top".
 */
export function resolveSectionedSortableDrop<TRow>(
	sections: SectionedSortableSection<TRow>[],
	getRowId: (row: TRow) => string,
	activeId: UniqueIdentifier,
	overId: UniqueIdentifier,
): SectionedSortableDrop<TRow> | null {
	if (activeId === overId) {
		return null;
	}

	if (isSectionDragId(activeId)) {
		if (!isSectionDragId(overId)) {
			return null;
		}

		const sectionIds = sections.map((section) => section.id);
		const from = sectionIds.indexOf(stripSectionDragId(activeId));
		const to = sectionIds.indexOf(stripSectionDragId(overId));
		if (from === -1 || to === -1 || from === to) {
			return null;
		}

		return {
			kind: "section",
			sections: arrayMove(sections, from, to),
			sectionId: sectionIds[from],
			toIndex: to,
		};
	}

	const rowId = stripRowDragId(activeId);
	const fromSection = sections.find((section) =>
		section.rows.some((row) => getRowId(row) === rowId),
	);
	const toSectionId = isSectionDragId(overId)
		? stripSectionDragId(overId)
		: sections.find((section) =>
				section.rows.some((row) => getRowId(row) === stripRowDragId(overId)),
			)?.id;

	if (!fromSection || fromSection.id !== toSectionId) {
		return null;
	}

	const from = fromSection.rows.findIndex((row) => getRowId(row) === rowId);
	const to = isSectionDragId(overId)
		? 0
		: fromSection.rows.findIndex(
				(row) => getRowId(row) === stripRowDragId(overId),
			);

	if (from === -1 || to === -1 || from === to) {
		return null;
	}

	return {
		kind: "row",
		sections: sections.map((section) =>
			section.id === fromSection.id
				? { ...section, rows: arrayMove(section.rows, from, to) }
				: section,
		),
		sectionId: fromSection.id,
		rowId,
		toIndex: to,
	};
}

type DragHandleProps = {
	label: string;
	disabled?: boolean;
	isDragging?: boolean;
	attributes: DraggableAttributes;
	listeners: DraggableSyntheticListeners;
};

/**
 * A real button rather than a draggable cell: it keeps a row's own click handlers from firing
 * on a drag, and gives keyboard users a focus target the KeyboardSensor can start from.
 */
const DragHandle = React.forwardRef<HTMLButtonElement, DragHandleProps>(
	({ label, disabled, isDragging, attributes, listeners }, ref) => (
		<button
			ref={ref}
			type="button"
			aria-label={label}
			disabled={disabled}
			data-interactive
			data-dragging={isDragging ? "true" : undefined}
			className={cn(
				"inline-flex size-6 cursor-grab items-center justify-center rounded text-neutral-500",
				"hover:bg-neutral-100 disabled:cursor-not-allowed disabled:text-neutral-200",
				"data-[dragging=true]:cursor-grabbing",
			)}
			{...attributes}
			{...listeners}
		>
			<GripVerticalIcon className="size-4" />
		</button>
	),
);
DragHandle.displayName = "DragHandle";

function RowCells<TRow>({ row }: { row: Row<TRow> }) {
	return (
		<>
			{row.getVisibleCells().map((cell) => (
				<Table.Cell
					key={cell.id}
					style={{ width: getColumnWidth(cell.column) }}
				>
					{flexRender(cell.column.columnDef.cell, cell.getContext())}
				</Table.Cell>
			))}
		</>
	);
}

type SortableRowProps<TRow> = {
	row: Row<TRow>;
	section: SectionedSortableSection<TRow>;
	disabled: boolean;
	getRowLabel?: (row: TRow) => string;
	renderRowActions?: (
		row: TRow,
		section: SectionedSortableSection<TRow>,
	) => React.ReactNode;
	testId?: string;
};

function SortableRow<TRow>({
	row,
	section,
	disabled,
	getRowLabel,
	renderRowActions,
	testId,
}: SortableRowProps<TRow>) {
	const {
		attributes,
		listeners,
		setNodeRef,
		setActivatorNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({
		id: rowDragId(row.id),
		data: { sectionId: section.id },
		disabled,
	});

	return (
		<Table.Row
			ref={setNodeRef}
			style={{ transform: CSS.Translate.toString(transform), transition }}
			className={cn(isDragging && "relative z-10 opacity-50")}
			data-testid={testId ? `${testId}-row-${row.id}` : undefined}
		>
			<Table.Cell className="w-[30px]">
				<DragHandle
					ref={setActivatorNodeRef}
					attributes={attributes}
					listeners={listeners}
					disabled={disabled}
					isDragging={isDragging}
					label={`Drag row ${getRowLabel ? getRowLabel(row.original) : row.id}`}
				/>
			</Table.Cell>
			<RowCells row={row} />
			<Table.Cell className="w-[60px] text-right">
				{renderRowActions?.(row.original, section)}
			</Table.Cell>
		</Table.Row>
	);
}

type SortableSectionBlockProps<TRow> = {
	section: SectionedSortableSection<TRow>;
	rows: Row<TRow>[];
	columnCount: number;
	isDisabled: boolean;
	areRowsDisabled: boolean;
	getRowLabel?: (row: TRow) => string;
	renderSectionActions?: (
		section: SectionedSortableSection<TRow>,
	) => React.ReactNode;
	renderRowActions?: (
		row: TRow,
		section: SectionedSortableSection<TRow>,
	) => React.ReactNode;
	testId?: string;
};

/**
 * The section header row plus its item rows.
 *
 * The header `<tr>` is the sortable node; the item rows are siblings rendered straight after it,
 * each with its OWN `useSortable` transform. The section's transform is never handed down to a
 * row: a row's transform is what opens the gap as it is dragged within its section, and an
 * inherited one would overwrite it.
 */
function SortableSectionBlock<TRow>({
	section,
	rows,
	columnCount,
	isDisabled,
	areRowsDisabled,
	getRowLabel,
	renderSectionActions,
	renderRowActions,
	testId,
}: SortableSectionBlockProps<TRow>) {
	const sectionDisabled = isDisabled || Boolean(section.disabled);
	const {
		attributes,
		listeners,
		setNodeRef,
		setActivatorNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({
		id: sectionDragId(section.id),
		disabled: sectionDisabled,
	});

	const rowIds = React.useMemo(
		() => rows.map((row) => rowDragId(row.id)),
		[rows],
	);

	return (
		<>
			<Table.Row
				ref={setNodeRef}
				style={{ transform: CSS.Translate.toString(transform), transition }}
				className={cn(
					"bg-primary-50/60 hover:bg-primary-50/60",
					isDragging && "relative z-20 opacity-50",
				)}
				data-testid={testId ? `${testId}-section-${section.id}` : undefined}
			>
				<Table.Cell className="w-[30px]">
					<DragHandle
						ref={setActivatorNodeRef}
						attributes={attributes}
						listeners={listeners}
						disabled={sectionDisabled}
						isDragging={isDragging}
						label={`Drag section ${section.label ?? section.id}`}
					/>
				</Table.Cell>
				<Table.Cell colSpan={columnCount}>{section.header}</Table.Cell>
				<Table.Cell className="w-[60px] text-right">
					{renderSectionActions?.(section)}
				</Table.Cell>
			</Table.Row>
			<SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
				{rows.map((row) => (
					<SortableRow
						key={row.id}
						row={row}
						section={section}
						disabled={isDisabled || areRowsDisabled}
						getRowLabel={getRowLabel}
						renderRowActions={renderRowActions}
						testId={testId}
					/>
				))}
			</SortableContext>
		</>
	);
}

const POINTER_SENSOR_OPTIONS = { activationConstraint: { distance: 4 } };
const KEYBOARD_SENSOR_OPTIONS = {
	coordinateGetter: sortableKeyboardCoordinates,
};
const DRAG_MODIFIERS = [restrictToVerticalAxis];

/**
 * A table whose rows sit under section headers, with two levels of drag: sections reorder as
 * blocks, and rows reorder within their own section only.
 *
 * A drop is applied locally straight away and reported through `onReorderSection` /
 * `onReorderRow`. Both get a `restore()` to call when persisting fails.
 *
 * @example
 * ```tsx
 * <SectionedSortableTable
 * 	sections={sections}
 * 	columns={columns}
 * 	getRowId={(item) => item.key}
 * 	isDisabled={isSaving}
 * 	onReorderSection={(sectionId, toIndex, restore) => save(sectionId, toIndex, restore)}
 * 	onReorderRow={(rowId, sectionId, toIndex, restore) => save(rowId, toIndex, restore)}
 * />
 * ```
 */
export function SectionedSortableTable<TRow>({
	sections,
	columns,
	getRowId,
	getRowLabel,
	onReorderSection,
	onReorderRow,
	renderSectionActions,
	renderRowActions,
	isDisabled = false,
	areRowsDisabled = false,
	isLoading = false,
	loadingRowCount = 3,
	emptyState = null,
	accessibility,
	"data-testid": testId,
}: SectionedSortableTableProps<TRow>) {
	const [pending, setPending] = React.useState<PendingOrder | null>(null);
	const [activeId, setActiveId] = React.useState<UniqueIdentifier | null>(null);

	const incomingOrder = React.useMemo(
		() => orderOf(sections, getRowId),
		[sections, getRowId],
	);

	// The optimistic order only stands while the incoming sections still look the way they did
	// at the drop. The moment the server answers with any other arrangement, that arrangement
	// wins: a server that placed the row somewhere else must not lose to a stale override.
	const order =
		pending && sameOrder(incomingOrder, pending.baseline)
			? pending.override
			: null;

	// Once the incoming sections leave the baseline the override is spent. Holding it would let
	// a later return to that baseline — another operator reordering back, or a refetch of an
	// older version — re-apply an arrangement the server never sent.
	if (pending && !sameOrder(incomingOrder, pending.baseline)) {
		setPending(null);
	}

	const displaySections = React.useMemo(
		() => applySectionedSortableOrder(sections, getRowId, order),
		[sections, getRowId, order],
	);

	const flatRows = React.useMemo(
		() => displaySections.flatMap((section) => section.rows),
		[displaySections],
	);

	// eslint-disable-next-line react-hooks/incompatible-library -- TanStack Table returns a non-memoizable instance
	const table = useReactTable({
		data: flatRows,
		columns,
		defaultColumn: AUTOSIZING_DEFAULT_COLUMN,
		getCoreRowModel: getCoreRowModel(),
		getRowId,
	});

	const { rowsById } = table.getRowModel();
	const columnCount = table.getVisibleFlatColumns().length;

	const sectionDragIds = React.useMemo(() => {
		if (process.env.NODE_ENV !== "production") {
			const seen = new Set<string>();
			for (const section of displaySections) {
				for (const row of section.rows) {
					const rowId = getRowId(row);
					if (seen.has(rowId)) {
						// eslint-disable-next-line no-console -- dev warning for a caller bug that breaks drag and drop
						console.warn(
							`[SectionedSortableTable] Duplicate row id "${rowId}". Row ids must be unique across all sections, or drag and drop will misbehave.`,
						);
						break;
					}
					seen.add(rowId);
				}
			}
		}

		return displaySections.map((section) => sectionDragId(section.id));
	}, [displaySections, getRowId]);

	const sensors = useSensors(
		useSensor(PointerSensor, POINTER_SENSOR_OPTIONS),
		useSensor(KeyboardSensor, KEYBOARD_SENSOR_OPTIONS),
	);

	// Resolving a section drag to a row leaves `over` somewhere a section can never land, so
	// section drags only see section droppables. Row drags go by pointer: `rectIntersection`
	// scores by intersection over union, which loses a tall section header to any grazed row.
	const collisionDetection = React.useCallback<CollisionDetection>((args) => {
		if (isSectionDragId(args.active.id)) {
			return rectIntersection({
				...args,
				droppableContainers: args.droppableContainers.filter((container) =>
					isSectionDragId(container.id),
				),
			});
		}

		const pointerCollisions = pointerWithin(args);
		if (pointerCollisions.length > 0) {
			return pointerCollisions;
		}

		// Keyboard dragging has no pointer, so rects are all there is.
		if (!args.pointerCoordinates) {
			return rectIntersection(args);
		}

		// Pointer is outside every row. Reporting nothing holds the last preview still.
		return [];
	}, []);

	// A row released over another section, or a section over a row, is refused and snaps back; so
	// does a drop that lands where the subject already was. dnd-kit runs onDragEnd before it
	// announces, so the announcement reads the outcome from here rather than re-deriving it
	// against sections an accepted drop has already moved.
	const dropChangedOrder = React.useRef(false);

	const handleDragStart = (event: DragStartEvent) =>
		setActiveId(event.active.id);

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		setActiveId(null);
		dropChangedOrder.current = false;
		if (!over) {
			return;
		}

		const drop = resolveSectionedSortableDrop(
			displaySections,
			getRowId,
			active.id,
			over.id,
		);
		if (!drop) {
			return;
		}
		dropChangedOrder.current = true;

		const previousPending = pending;
		const restore = () => setPending(previousPending);
		setPending({
			baseline: incomingOrder,
			override: orderOf(drop.sections, getRowId),
		});

		if (drop.kind === "section") {
			onReorderSection(drop.sectionId, drop.toIndex, restore);
			return;
		}
		onReorderRow(drop.rowId, drop.sectionId, drop.toIndex, restore);
	};

	// dnd-kit's default announcements read the raw namespaced drag id ("row:itm_01J…"). Name
	// the subject instead, from the same labels the drag handles use.
	const labelOf = (id: UniqueIdentifier) => {
		if (isSectionDragId(id)) {
			const section = displaySections.find(
				(candidate) => candidate.id === stripSectionDragId(id),
			);
			return section ? `section ${section.label ?? section.id}` : String(id);
		}
		const row = rowsById[stripRowDragId(id)];
		if (!row) {
			return String(id);
		}
		return `item ${getRowLabel ? getRowLabel(row.original) : row.id}`;
	};

	const announcements: Announcements = {
		onDragStart: ({ active }) => `Picked up ${labelOf(active.id)}.`,
		onDragOver: ({ active, over }) =>
			over
				? `${labelOf(active.id)} is over ${labelOf(over.id)}.`
				: `${labelOf(active.id)} is no longer over a drop target.`,
		onDragEnd: ({ active, over }) =>
			over && dropChangedOrder.current
				? `${labelOf(active.id)} was dropped onto ${labelOf(over.id)}.`
				: `${labelOf(active.id)} was not moved.`,
		onDragCancel: ({ active }) =>
			`Dragging ${labelOf(active.id)} was cancelled.`,
	};
	const activeSection =
		activeId && isSectionDragId(activeId)
			? displaySections.find(
					(section) => section.id === stripSectionDragId(activeId),
				)
			: undefined;
	const activeRow =
		activeId && !isSectionDragId(activeId)
			? rowsById[stripRowDragId(activeId)]
			: undefined;

	return (
		<DndContext
			sensors={sensors}
			modifiers={DRAG_MODIFIERS}
			collisionDetection={collisionDetection}
			onDragStart={handleDragStart}
			onDragEnd={handleDragEnd}
			onDragCancel={() => setActiveId(null)}
			accessibility={{ announcements, ...accessibility }}
		>
			<Table data-testid={testId}>
				<Table.Header data-testid={testId ? `${testId}-header` : undefined}>
					<Table.Column className="w-[30px]" />
					{table.getHeaderGroups()[0]?.headers.map((header) => (
						<Table.Column
							key={header.id}
							style={{ width: getColumnWidth(header.column) }}
						>
							{header.isPlaceholder
								? null
								: flexRender(
										header.column.columnDef.header,
										header.getContext(),
									)}
						</Table.Column>
					))}
					<Table.Column className="w-[60px]" />
				</Table.Header>
				<Table.Body>
					{isLoading ? (
						Array.from({ length: loadingRowCount }).map((_, index) => (
							<Table.Row key={`loading-${index}`}>
								<Table.Cell colSpan={columnCount + 2}>
									<Skeleton className="my-[5px] flex h-[13px] items-center rounded-md" />
								</Table.Cell>
							</Table.Row>
						))
					) : displaySections.length === 0 ? (
						<Table.Row>
							<Table.Cell
								colSpan={columnCount + 2}
								className="h-[200px] text-center"
							>
								<div className="flex min-h-[200px] items-center justify-center">
									{emptyState}
								</div>
							</Table.Cell>
						</Table.Row>
					) : (
						<SortableContext
							items={sectionDragIds}
							strategy={verticalListSortingStrategy}
						>
							{displaySections.map((section) => (
								<SortableSectionBlock
									key={section.id}
									section={section}
									rows={section.rows.flatMap((row) => {
										const tableRow = rowsById[getRowId(row)];
										return tableRow ? [tableRow] : [];
									})}
									columnCount={columnCount}
									isDisabled={isDisabled}
									areRowsDisabled={areRowsDisabled}
									getRowLabel={getRowLabel}
									renderSectionActions={renderSectionActions}
									renderRowActions={renderRowActions}
									testId={testId}
								/>
							))}
						</SortableContext>
					)}
				</Table.Body>
			</Table>
			<DragOverlay>
				{activeSection || activeRow ? (
					<Table className="bg-background opacity-90 shadow-md">
						<Table.Body>
							<Table.Row className={cn(activeSection && "bg-primary-50/60")}>
								<Table.Cell className="w-[30px]">
									<GripVerticalIcon className="size-4 text-neutral-500" />
								</Table.Cell>
								{activeSection ? (
									<Table.Cell colSpan={columnCount}>
										{activeSection.header}
									</Table.Cell>
								) : (
									<RowCells row={activeRow!} />
								)}
								<Table.Cell className="w-[60px]">{null}</Table.Cell>
							</Table.Row>
						</Table.Body>
					</Table>
				) : null}
			</DragOverlay>
		</DndContext>
	);
}
